/**
 * <s-booking-survey>: one-question-per-screen trek booking survey.
 * Receives `--next` / `--back` commands from its nav buttons, validates each
 * step, and on the final screen builds a prefilled WhatsApp message from the answers.
 */
class BookingSurvey extends HTMLElement {
  #index = 0;
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.form = this.querySelector('[data-survey-form]');
    this.steps = [...this.querySelectorAll('[data-step]')];
    this.addEventListener('command', (event) => {
      if (event.command === '--next') this.#go(1);
      if (event.command === '--back') this.#go(-1);
    }, { signal });
    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#go(1);
    }, { signal });
    this.form?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && event.target.tagName === 'INPUT') {
        event.preventDefault();
        this.#go(1);
      }
    }, { signal });
    this.#preselectMonth();
    this.#render(false);
  }

  // A month tapped on the trek page's availability calendar arrives as ?month=Dec+2026.
  #preselectMonth() {
    const month = new URLSearchParams(window.location.search).get('month');
    if (!month) return;
    const choice = [...this.querySelectorAll('input[name="when"]')].find((input) => input.value === month);
    if (choice) choice.checked = true;
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #go(delta) {
    const next = this.#index + delta;
    if (next < 0 || next >= this.steps.length) return;
    if (delta > 0 && !this.#validate(this.steps[this.#index])) return;
    this.#index = next;
    if (this.steps[next].hasAttribute('data-step-done')) this.#buildMessage();
    this.#render(true);
  }

  #validate(step) {
    const fields = [...step.querySelectorAll('input, textarea')];
    const invalid = fields.find((field) => !field.checkValidity());
    if (invalid) {
      invalid.reportValidity();
      invalid.focus();
      return false;
    }
    return true;
  }

  #render(moveFocus) {
    const questionCount = this.steps.filter((s) => !s.hasAttribute('data-step-done')).length;
    const isDone = this.steps[this.#index].hasAttribute('data-step-done');
    this.steps.forEach((step, i) => { step.hidden = i !== this.#index; });

    const current = Math.min(this.#index + 1, questionCount);
    const label = this.querySelector('[data-survey-progress-label]');
    const bar = this.querySelector('[data-survey-progress-bar]');
    if (label) label.textContent = (this.dataset.progress || '').replace('{current}', current).replace('{total}', questionCount);
    if (bar) bar.style.width = `${isDone ? 100 : (current / questionCount) * 100}%`;

    const nav = this.querySelector('[data-survey-nav]');
    const back = this.querySelector('[data-survey-back]');
    if (nav) nav.hidden = isDone;
    if (back) back.hidden = this.#index === 0;

    if (moveFocus) {
      const target = this.steps[this.#index].querySelector('input, textarea, h2, [data-survey-whatsapp]');
      if (target && !target.matches('input, textarea')) target.setAttribute('tabindex', '-1');
      target?.focus({ preventScroll: false });
    }
  }

  #buildMessage() {
    const lines = [`${this.dataset.intro} ${this.dataset.trek} (${this.dataset.trekUrl})`, ''];
    this.steps.forEach((step) => {
      if (step.hasAttribute('data-step-done')) return;
      const checked = step.querySelector('input:checked');
      const field = checked || step.querySelector('input:not([type="radio"]), textarea');
      const value = field?.value?.trim();
      if (value) lines.push(`${step.dataset.label}: ${value}`);
    });
    const link = this.querySelector('[data-survey-whatsapp]');
    if (link) link.href = `https://wa.me/${this.dataset.whatsapp}?text=${encodeURIComponent(lines.join('\n'))}`;
  }
}

if (!customElements.get('s-booking-survey')) customElements.define('s-booking-survey', BookingSurvey);
